import './App.css';
import techBackground from './assets/tech-background.png';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from 'components/ui/sidebar';
import AppSidebar from 'components/AppSidebar';
import Dashboard from './pages/Dashboard';
import Sagemcom from './pages/Sagemcom';
import Logistics from './pages/Logistics';
import BTSProjectPage from './pages/BTSProjectPage';
import PurchaseOrders from './pages/PurchaseOrders';
import InvoiceTracking from './pages/InvoiceTracking';
import DeliveryTracking from './pages/DeliveryTracking';
import LocalTransportationTrucking from './pages/LocalTransportationTrucking';
import MaterialInventory from './pages/MaterialInventory';
import ToolsInventory from './pages/ToolsInventory';
import Stationaries from './pages/Stationaries';
import Suppliers from './pages/Suppliers';
import VehicleReport from './pages/VehicleReport';
import FuelReport from './pages/FuelReport';
import PaymentTrucking from './pages/PaymentTrucking';
import CarRentalInvoicing from './pages/CarRentalInvoicing';
import FleetMaintenanceCostTracking from './pages/FleetMaintenanceCostTracking';
import Procedure from './pages/Procedure';
import TaskManagement from './pages/TaskManagement';
import CustomerServiceKenya from './pages/CustomerServiceKenya';
import Team from './pages/Team';
import TeamUserAccess from './pages/TeamUserAccess';
import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { SHOW_MATERIAL_INVENTORY } from './config/featureFlags';
import {
  logout as authLogout,
  ROLE_DRIVER,
} from './services/authService';
import { useAuthSession } from './contexts/AuthSessionContext';

const SIDEBAR_COLLAPSED_KEY = 'striitsmart:sidebarCollapsed';

function AppContent() {
  const authSession = useAuthSession();
  const isDriverAccount = authSession?.role === ROLE_DRIVER;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (isDriverAccount) return true;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();

  const handleLogout = async () => {
    await authLogout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    document.documentElement.classList.toggle('app-driver-mode', Boolean(isDriverAccount));
    return () => document.documentElement.classList.remove('app-driver-mode');
  }, [isDriverAccount]);

  useEffect(() => {
    if (isDriverAccount) setSidebarCollapsed(true);
  }, [isDriverAccount]);

  return (
    <SidebarProvider open={!sidebarCollapsed} onOpenChange={(open) => setSidebarCollapsed(!open)}>
      <div className="App">
        <div
          className={`dashboard${sidebarCollapsed ? ' sidebar-collapsed' : ''}${isDriverAccount ? ' dashboard--driver' : ''}`}
          style={{ '--app-bg-image': `url(${techBackground})` }}
        >
          <AppSidebar authSession={authSession} onLogout={handleLogout} />

          <div className="main-content">
            <div className="app-sidebar-toggle-wrap">
              <SidebarTrigger
                className="app-sidebar-toggle"
                title="Toggle navigation sidebar"
              />
            </div>
            <div className="global-sagemcom-logo" aria-label="Sagemcom logo">
              Sagemcom
            </div>
            <div className="content-overlay">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/sagemcom" element={<Sagemcom />} />
                <Route path="/sagemcom/logistics" element={<Logistics />} />
                <Route path="/sagemcom/logistics/purchase-orders" element={<PurchaseOrders />} />
                <Route path="/sagemcom/logistics/invoice-tracking" element={<InvoiceTracking />} />
                <Route path="/sagemcom/logistics/delivery-tracking" element={<DeliveryTracking />} />
                <Route path="/sagemcom/logistics/local-transportation-trucking" element={<LocalTransportationTrucking />} />
                <Route path="/sagemcom/logistics/poles-po-quantities" element={<Navigate to="/sagemcom/logistics/delivery-tracking" replace />} />
                <Route
                  path="/sagemcom/logistics/material-inventory"
                  element={
                    SHOW_MATERIAL_INVENTORY ? (
                      <MaterialInventory />
                    ) : (
                      <Navigate to="/sagemcom/logistics/delivery-tracking" replace />
                    )
                  }
                />
                <Route path="/sagemcom/logistics/tools-inventory" element={<ToolsInventory />} />
                <Route
                  path="/sagemcom/logistics/assets-inventory"
                  element={<Navigate to="/sagemcom/logistics/purchase-orders" replace />}
                />
                <Route path="/sagemcom/logistics/stationaries" element={<Stationaries />} />
                <Route path="/sagemcom/logistics/suppliers" element={<Suppliers />} />
                <Route path="/sagemcom/fleet-management/fleet-maintenance-cost-tracking" element={<Navigate to="/sagemcom/fleet-management" replace />} />
                <Route path="/sagemcom/fleet-management" element={<FleetMaintenanceCostTracking />} />
                <Route path="/sagemcom/fleet-management/bts" element={<BTSProjectPage />} />
                <Route path="/sagemcom/fleet-management/vehicle-report" element={<VehicleReport />} />
                <Route path="/sagemcom/fleet-management/fuel-report" element={<FuelReport />} />
                <Route path="/sagemcom/fleet-management/payment-trucking" element={<PaymentTrucking />} />
                <Route path="/sagemcom/fleet/car-rental-invoicing" element={<CarRentalInvoicing />} />
                <Route path="/procedure" element={<Procedure />} />
                <Route path="/task-management" element={<TaskManagement />} />
                <Route path="/customer-service-kenya" element={<CustomerServiceKenya />} />
                <Route path="/team" element={<Team />} />
                <Route path="/team/user-access" element={<TeamUserAccess />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={(
            <ProtectedRoute>
              <AppContent />
            </ProtectedRoute>
          )}
        />
      </Routes>
    </Router>
  );
}

export default App;
